import * as React from 'react';
import cx from 'classnames';
import { I18n } from 'react-redux-i18n';

import TreeDropdown from '@common/components/TreeDropdown';
import { getTestAttributes } from '@common/utils/tests';

import './folder.styles.scss';
import {NewTableColumnFilterOption} from "@common/components/new-table";

export type FolderFilterProps = {
    moduleName: string;
    value?: NewTableColumnFilterOption['actualValue'];
    className?: string;
    options?: Array<NewTableColumnFilterOption>;
    metadata: { property: string };
    onSave: (params: { value: NewTableColumnFilterOption['actualValue']; metadata: { property: string } }) => void;
    getFolders?: () => Promise<Array<any>>; // Folder data structure
};

export const FolderFilter: React.FC<FolderFilterProps> = (props) => {
    const { value, className, metadata, onSave, moduleName, getFolders } = props;
    const [folders, setFolders] = React.useState<Array<any>>([]);
    const [loading, setLoading] = React.useState(false);
    const [folderNameToIdMap, setFolderNameToIdMap] = React.useState<Record<string, number>>({});
    const [folderIdToNameMap, setFolderIdToNameMap] = React.useState<Record<number, string>>({});


    // Load folders when component mounts or when getFolders changes
    React.useEffect(() => {

        if (getFolders) {
            setLoading(true);
            getFolders()
                .then((folderData) => {
                    setFolders(folderData);

                    // Create mapping between folder names and IDs
                    const nameToIdMap: Record<string, number> = {};
                    const idToNameMap: Record<number, string> = {};

                    const processFolders = (folderList: any[]) => {
                        folderList.forEach(folder => {
                            nameToIdMap[folder.name] = folder.id;
                            idToNameMap[folder.id] = folder.name;
                            if (folder.children) {
                                processFolders(folder.children);
                            }
                        });
                    };

                    processFolders(folderData);
                    setFolderNameToIdMap(nameToIdMap);
                    setFolderIdToNameMap(idToNameMap);
                })
                .catch((error) => {
                    console.error('Failed to load folders:', error);
                    setFolders([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            console.log('getFolders function is NOT provided');
        }
    }, [getFolders]);

    const handleChange = React.useCallback((selectedValue: NewTableColumnFilterOption['actualValue'], selectedFolder?: any) => {

        // Return the folder name for filtering
        const folderName = selectedFolder?.name || selectedValue;

        onSave({
            value: folderName, // Use folder name for filtering
            metadata,
        });
    }, [onSave, metadata]);

    // Convert folder name to ID for TreeDropdown value
    const getTreeDropdownValue = () => {
        if (!value) return undefined;

        // If value is a folder name, convert to ID
        if (typeof value === 'string' && folderNameToIdMap[value]) {
            return folderNameToIdMap[value];
        }

        // If value is already an ID, return as is
        return value;
    };

    if (loading) {
        return (
            <div className={cx('TableFolderFilter', className)}>
                <div className="TableFolderFilter__loading">
                    {I18n.t('Loading folders...')}
                </div>
            </div>
        );
    }

    if (!folders.length) {
        return (
            <div className={cx('TableFolderFilter', className)}>
                <div className="TableFolderFilter__no-folders">
                    {I18n.t('No folders available')}
                </div>
            </div>
        );
    }

    return (
        <TreeDropdown
            className={cx('TableFolderFilter', className)}
            items={folders}
            value={getTreeDropdownValue()} // Use mapped value
            placeholder={I18n.t('Select Folder')}
            onChange={handleChange}
            {...getTestAttributes({ id: `${moduleName}-folder-filter-dropdown` })}
        />
    );
};

FolderFilter.defaultProps = {
    options: [],
};